/**
 * `/__ui/views` — the three round-5u renderers running on the **in-memory** fixture source,
 * with no session, no api and no database (the trick B4's board harness uses, M1-B4 §5).
 *
 * It exists so `tools/browser/verify-views-local.mjs` can hit-test the measured geometry on a
 * served build: a kanban lane header is 260 × 40, a card 240 wide, a calendar pill 21 tall, a
 * Cards grid three across. A box is not a render — every claim is checked with
 * `elementFromPoint` — and that check needs a real page.
 *
 *   `?screen=kanban` (default) · `calendar` · `gallery`
 *   `?cover=1` puts a cover column on the cards · `?collapsed=<bucketKey>`
 */
import {
  DEFAULT_CALENDAR_VIEW_SETTINGS,
  DEFAULT_GALLERY_VIEW_SETTINGS,
  DEFAULT_KANBAN_VIEW_SETTINGS,
  type CalendarViewSettings,
  type GalleryViewSettings,
  type KanbanViewSettings,
} from '@elizmonday/shared';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { createFixtureDataSource, FIXTURE_BOARD_ID, FIXTURE_VIEW_ID } from '../board-view/data/fixture';
import { CalendarView } from './calendar/CalendarView';
import { GalleryView } from './gallery/GalleryView';
import { KanbanView } from './kanban/KanbanView';

export function ViewsHarness() {
  const [params] = useSearchParams();
  const screen = params.get('screen') ?? 'kanban';
  const cover = params.get('cover') !== null;
  const collapsed = params.get('collapsed');
  const source = useMemo(() => createFixtureDataSource(), []);

  const [kanban, setKanban] = useState<KanbanViewSettings>({
    ...DEFAULT_KANBAN_VIEW_SETTINGS,
    pivotColumnId: 'status',
    cardColumns: { all: false, byColumn: { date: true } },
    coverColumns: cover ? { all: false, byColumn: { files: true } } : DEFAULT_KANBAN_VIEW_SETTINGS.coverColumns,
    collapsedLaneKeys: collapsed === null ? [] : [collapsed],
  });
  const [calendar, setCalendar] = useState<CalendarViewSettings>({
    ...DEFAULT_CALENDAR_VIEW_SETTINGS,
    dateColumns: { all: false, byColumn: { date: true } },
    colorColumnId: 'status',
  });
  const [gallery, setGallery] = useState<GalleryViewSettings>({
    ...DEFAULT_GALLERY_VIEW_SETTINGS,
    cardColumns: { all: false, byColumn: { status: true, date: true, text: true } },
    coverColumnId: cover ? 'files' : null,
  });

  const boardId = String(FIXTURE_BOARD_ID);
  const viewId = String(FIXTURE_VIEW_ID);

  return (
    <div className="ez-board-host" data-testid={`views-harness-${screen}`} style={{ height: '100vh' }}>
      {screen === 'calendar' ? (
        <CalendarView
          boardId={boardId}
          viewId={viewId}
          source={source}
          settingsOverride={{
            settings: calendar,
            save: (patch) => setCalendar((previous) => ({ ...previous, ...patch })),
          }}
        />
      ) : screen === 'gallery' ? (
        <GalleryView
          boardId={boardId}
          viewId={viewId}
          source={source}
          settingsOverride={{
            settings: gallery,
            save: (patch) => setGallery((previous) => ({ ...previous, ...patch })),
          }}
        />
      ) : (
        <KanbanView
          boardId={boardId}
          viewId={viewId}
          source={source}
          settingsOverride={{
            settings: kanban,
            save: (patch) => setKanban((previous) => ({ ...previous, ...patch })),
          }}
        />
      )}
    </div>
  );
}
